<template>
  <v-app class="ee-modern" :class="{ 'ee-dark': dark }">
    <!-- app bar as in the prototype: back on every pushed screen, a large title that scrolls away
         and a compact centered title in the bar once it has (the large title is in the page flow,
         so the sticky bar never changes height while scrolling) -->
    <header
      v-if="!isLogin && !isHome"
      class="ee-appbar"
      :class="{ 'is-scrolled': scrolled, 'is-compact': compact || simpleTitle }"
    >
      <div class="ee-appbar__row">
        <button v-if="showBack" type="button" class="ee-icon-btn" :aria-label="tm('back', 'Back')" @click="back">
          <span class="material-symbols-outlined">chevron_left</span>
        </button>
        <div v-else class="ee-appbar__spacer" />
        <div class="ee-appbar__title">{{ pageTitle }}</div>
        <button
          v-for="btn in toolbarButtons"
          :key="btn.path"
          type="button"
          class="ee-icon-btn"
          @click="navigateTo(btn.path)"
        >
          <span class="material-symbols-outlined">{{ btn.icon }}</span>
        </button>
        <bell v-if="showNotifications" />
        <avatar-button />
      </div>
      <div v-if="showToolbarTabs" class="ee-appbar__tabs">
        <tabs />
      </div>
    </header>
    <h1 v-if="!isLogin && !isHome && !simpleTitle && pageTitle" ref="largeTitle" class="ee-large-title">{{ pageTitle }}</h1>

    <v-main class="ee-main" :class="{ 'ee-main--bare': isLogin || fullScreen }">
      <!-- /login keeps its route (redirects rely on it) but renders the modern screen -->
      <modern-login v-if="isLogin" />
      <template v-else>
        <in-page-messages />
        <transition name="ee-fade">
          <router-view :key="viewKey" />
        </transition>
      </template>
    </v-main>

    <tab-bar v-if="!isLogin && !fullScreen" />

    <v-snackbar v-model="snack.show" class="ee-snack" :timeout="3500" bottom>
      {{ snack.text }}
      <template v-if="snack.action" v-slot:action="{ attrs }">
        <v-btn text color="primary" v-bind="attrs" @click="runSnackAction">{{ snack.action.label }}</v-btn>
      </template>
    </v-snackbar>
  </v-app>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import navigateMixin from '../mixins/navigate'
import i18nMixin from '../modern/i18n'
import { isDark, onSystemThemeChange } from '../modern/theme'
import Tabs from '../components/toolbar/tabs'
import InPageMessages from '../components/modern/InPageMessage'
import Bell from '../components/modern/Bell'
import AvatarButton from '../components/modern/AvatarButton'
import TabBar from '../components/modern/TabBar'
import ModernLogin from '../components/modern/Login'

export default {
  mixins: [navigateMixin, i18nMixin],

  components: {
    Tabs,
    InPageMessages,
    Bell,
    AvatarButton,
    TabBar,
    ModernLogin,
  },

  data () {
    return {
      scrolled: false,
      compact: false,
      dark: isDark(),
      // in-app history depth: back goes to the previous app screen, otherwise to the parent / home
      historyDepth: 0,
      goingBack: false,
      snack: { show: false, text: '', action: null },
    }
  },

  computed: {
    ...mapGetters({
      toolbarButtons: 'buttons',
    }),

    ...mapGetters('common', ['showNotifications', 'showToolbarTabs']),

    ...mapState({
      user: state => state.auth.user,
      pageTitle: state => state.navbar.title,
      parent: state => state.navbar.parent,
    }),

    // a new screen per address, except the open talk of the programme (?talk): the page stays as it is
    viewKey () {
      const { talk, ...query } = this.$route.query
      return this.$router.resolve({ path: this.$route.path, query }).href
    },

    isHome () {
      return this.$route.path === '/'
    },

    isLogin () {
      return this.$route.path === '/login'
    },

    // back everywhere except home (tab roots go back in history, or home)
    showBack () {
      return (this.$route.path.replace(/^\/room-[^/]+/, '') || '/') !== '/'
    },

    // chats and screens with toolbar tabs keep just the compact title
    simpleTitle () {
      return this.fullScreen || this.showToolbarTabs
    },

    // chats have their composer pinned to the bottom: no tab bar there (as in the prototype)
    fullScreen () {
      if (['networking-chat', 'questions-form', 'questions-form-parent'].includes(this.$route.name)) return true
      const path = this.$route.path.replace(/^\/room-[^/]+/, '')
      return this.$store.state.common.menu.some(item => item.module === 'chat' && path === `/${item.slug}`)
    },
  },

  watch: {
    $route (to, from) {
      // only the query changed (e.g. the open talk in the programme): not a new screen
      if (to.path === from.path) return
      if (this.goingBack) {
        this.historyDepth = Math.max(0, this.historyDepth - 1)
        this.goingBack = false
      } else if (from.matched.length) {
        this.historyDepth++
      }
      this.$nextTick(this.onScroll)
    },
  },

  mounted () {
    this.onScroll = () => {
      this.scrolled = window.scrollY > 4
      const large = this.$refs.largeTitle
      this.compact = large ? window.scrollY > large.offsetHeight - 8 : false
    }
    window.addEventListener('scroll', this.onScroll, { passive: true })
    // browser / Android back button
    this.onPopState = () => {
      this.goingBack = true
    }
    window.addEventListener('popstate', this.onPopState)

    // appearance (profile -> Appearance); Vuetify components follow the same theme
    this.applyTheme = () => {
      this.dark = isDark()
      this.$vuetify.theme.dark = this.dark
    }
    this.applyTheme()
    this.$root.$on('ee-theme', this.applyTheme)
    this.offSystemTheme = onSystemThemeChange(this.applyTheme)

    // app-wide confirmations: this.$root.$emit('ee-snack', { text, action: { label, handler } })
    this.$root.$on('ee-snack', ({ text, action }) => {
      this.snack = { show: true, text, action }
    })
  },

  beforeDestroy () {
    window.removeEventListener('scroll', this.onScroll)
    window.removeEventListener('popstate', this.onPopState)
    this.$root.$off('ee-theme', this.applyTheme)
    this.offSystemTheme()
    this.$root.$off('ee-snack')
  },

  methods: {
    back () {
      if (this.historyDepth > 0) {
        this.goingBack = true
        this.$router.back()
      } else if (this.parent) {
        this.toParent()
      } else {
        this.navigateTo('/')
      }
    },

    runSnackAction () {
      this.snack.show = false
      this.snack.action.handler()
    },
  },
}
</script>
